import { Drawer, Stack, Paper, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddEventForm from './AddEditEventForm';
import { CalendarEvent } from '@src/types/Events';
import { Stack as FracStack } from '@src/types/Stacks';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { generateValues } from './FormSections/generateValues';
import QueryKeys from '@src/constants/queryKeys';
import useEventMutations from '@src/hooks/api/mutations/useEventMutations';
import { useQueryClient } from '@tanstack/react-query';

interface EventEditorWindowProps {
  dialogOpen: boolean;
  activeEvent: CalendarEvent;
  stacksList: FracStack[];
  onClose: () => void;
  setError: (error: string | null) => void;
  setDialogOpen: (open: boolean) => void;
  setConfirmOpen: (open: boolean) => void;
}

const eventSchema = yup.object().shape({
  title: yup.string().required('Title is required'),
  start: yup.string().required('Start date is required'),
  end: yup.string().default(''),
  rrule: yup
    .object()
    .default({
      freq: null,
      interval: null,
      byweekday: null,
      dtstart: null,
      until: null,
    })
    .shape({
      freq: yup.string().nullable(),
      interval: yup.number().nullable(),
      byweekday: yup.array().of(yup.string()).nullable(),
      dtstart: yup.string(),
      until: yup.string().nullable(),
    }),
  duration: yup.string().required('Duration is required'),
  backgroundColor: yup.string().required('Background color is required'),
  stackId: yup.string().required('Stack ID is required'),
  startCommands: yup.array().of(yup.string()).nullable().optional(),
  stopCommands: yup.array().of(yup.string()).nullable().optional(),
});

export default function EventEditorWindow({
  dialogOpen,
  activeEvent,
  stacksList,
  onClose,
  setError,
  setDialogOpen,
  setConfirmOpen,
}: EventEditorWindowProps) {
  const methods = useForm<CalendarEvent>({
    defaultValues: generateValues(activeEvent),
    resolver: yupResolver(eventSchema) as Resolver<CalendarEvent>,
  });
  const { createCalendarEvent, updateCalendarEvent } = useEventMutations(
    activeEvent?.id,
  );
  const queryClient = useQueryClient();

  const handleSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.events],
      exact: false,
    });

    setDialogOpen(false);
    setConfirmOpen(true);
  };

  const handleEventUpdates = (eventToSubmit: CalendarEvent) => {
    if (activeEvent?.id) {
      updateCalendarEvent.mutate(eventToSubmit, {
        onSuccess: handleSuccess,
      });
    } else {
      createCalendarEvent.mutate(eventToSubmit, {
        onSuccess: handleSuccess,
      });
    }
  };

  return (
    <Drawer
      anchor='right'
      open={dialogOpen}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      PaperProps={{
        sx: {
          bgcolor: '#212327',
        },
      }}
      variant='persistent'
    >
      <Stack sx={{ width: 700 }} flex={1}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          component={Paper}
          sx={{
            backgroundColor: '#4d5256',
            borderRadius: 0,
          }}
          elevation={0}
        >
          <Typography variant='h6' component='h2' sx={{ p: 2 }}>
            {activeEvent ? 'Edit Event' : 'Add Event'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <FormProvider {...methods}>
          <AddEventForm
            activeEvent={activeEvent}
            onClose={onClose}
            stacks={stacksList}
            setError={setError}
            setDialogOpen={setDialogOpen}
            setConfirmOpen={setConfirmOpen}
            handleEventUpdates={handleEventUpdates}
          />
        </FormProvider>
      </Stack>
    </Drawer>
  );
}
